import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EmptyStateComponent } from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IContactNumber,
  IPatient,
  IReferral,
  OptionalPatientDetails,
  ReferralPatientDetails,
  PrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  DAY_MONTH_YEAR_FORMAT,
  filterUndefined,
  WithRef,
} from '@principle-theorem/shared';
import { isEqual } from 'lodash';
import { MomentModule } from 'ngx-moment';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as moment from 'moment-timezone';

@Component({
  selector: 'pr-referral-patient-details',
  imports: [
    NgCommonModule,
    MomentModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    EmptyStateComponent,
    MatSlideToggleModule,
  ],
  templateUrl: './referral-patient-details.component.html',
  styleUrl: './referral-patient-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralPatientDetailsComponent {
  private _destroyRef = inject(DestroyRef);
  private _clipboard = inject(Clipboard);
  private _snackbar = inject(MatSnackBar);

  readonly dateFormat = DAY_MONTH_YEAR_FORMAT;
  referral = input.required<WithRef<IReferral>>();
  patient = input<WithRef<IPatient>>();
  contactNumbers = input<IContactNumber[]>([]);
  primaryContact = input<PrimaryContact | undefined>(undefined);
  editable = input<boolean>(false);
  sharedPatientDetailsChanges = output<OptionalPatientDetails>();
  openPatientInNewTab = output<string>();

  patientDetails = computed<
    WithRef<IPatient> | ReferralPatientDetails | undefined
  >(() =>
    this.editable()
      ? this.patient()
      : this.referral().patientDetails || undefined
  );

  displayContactNumbers = computed(() =>
    this.editable()
      ? this.contactNumbers()
      : this.referral().patientDetails.contactNumbers || []
  );

  displayPrimaryContact = computed(() =>
    this.editable()
      ? this.primaryContact()
      : this.referral().patientDetails.primaryContact || undefined
  );

  optionalPatientDetailsForm = new TypedFormGroup<OptionalPatientDetails>({
    includePhone: new TypedFormControl<boolean>(true),
    includeEmail: new TypedFormControl<boolean>(true),
    includeAddress: new TypedFormControl<boolean>(true),
  });

  constructor() {
    toObservable(this.referral)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((referral) =>
        this._initialiseFormWithReferralValues(referral)
      );

    this._handleFormChanges();
  }

  toggleField(field: keyof OptionalPatientDetails): void {
    const currentValue = this.optionalPatientDetailsForm.value[field];
    this.optionalPatientDetailsForm.patchValue({
      [field]: !currentValue,
    } as Partial<OptionalPatientDetails>);
  }

  copyPatientDetailsToClipboard(): void {
    const details = this.patientDetails();
    if (!details) {
      return;
    }

    const contactNumbers = this.displayContactNumbers();
    const primaryContact = this.displayPrimaryContact();
    const formValues = this.optionalPatientDetailsForm.value;

    const lines: string[] = [];

    lines.push(`Name: ${details.name}`);

    if ('dateOfBirth' in details) {
      const dob = moment(details.dateOfBirth);
      lines.push(`Date of Birth: ${dob.format(this.dateFormat)}`);
    }

    if (primaryContact) {
      lines.push(
        `Primary Contact: ${primaryContact.patient.name} (${primaryContact.type})`
      );
    }

    if (formValues.includePhone && contactNumbers.length > 0) {
      const phoneNumbers = contactNumbers
        .map((num) => `${num.number}${num.label ? ` (${num.label})` : ''}`)
        .join(', ');
      lines.push(`Phone: ${phoneNumbers}`);
    }

    if (formValues.includeEmail && 'email' in details && details.email) {
      lines.push(`Email: ${details.email}`);
    }

    if (formValues.includeAddress && 'address' in details && details.address) {
      lines.push(`Address: ${details.address}`);
    }

    const text = lines.join('\n');
    const success = this._clipboard.copy(text);

    if (success) {
      this._snackbar.open('Patient details copied to clipboard', undefined, {
        duration: 3000,
      });
    } else {
      this._snackbar.open('Failed to copy to clipboard', undefined, {
        duration: 3000,
      });
    }
  }

  private _handleFormChanges(): void {
    this.optionalPatientDetailsForm.valueChanges
      .pipe(
        distinctUntilChanged(isEqual),
        debounceTime(500),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((options: OptionalPatientDetails) => {
        if (!this.editable()) {
          return;
        }
        this.sharedPatientDetailsChanges.emit(options);
      });
  }

  private _initialiseFormWithReferralValues(
    referral: WithRef<IReferral>
  ): void {
    this.optionalPatientDetailsForm.patchValue(
      {
        includePhone: !!referral.patientDetails.includePhone,
        includeEmail: !!referral.patientDetails.includeEmail,
        includeAddress: !!referral.patientDetails.includeAddress,
      },
      { emitEvent: false }
    );
  }
}
